import React, { useState, useEffect, useRef } from "react";
import NoImage from "../../../images/no-image.jpg";
import {
  createBank,
  getBankById,
  updateBank,
} from "../../../services/BankService";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import PageTitle from "../../layouts/PageTitle";
import { Button } from "react-bootstrap";
import {
  createHomeDetails,
  getHomeDetail,
  updateHomeDetails,
} from "../../../services/homeService";
import CkEditorBlog from "../Forms/CkEditor/CkEditorBlog";
import HeroMediaPreview from "../common/HeroMediaPreview";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import CustomClassicEditor from "../CustomClassicEditor";

const HomePage = () => {
  const [formData, setFormData] = useState({
    hero_section_title: "",
    hero_section_desc: "",
    button_desc: "",
    highlights: [],
    highlights_enabled: true,
    infotitle: "",
    desc: "",
    how_use_title: "",
    how_use_desc: "",
    about_title: "",
    about_desc: "",
    aboutfeatures: [],
    why_choose_title: "",
    why_choose_desc: "",
    features: [],
    advantages: [],
    tollfreenumber: "",
    facebooklink: "",
    twitterlink: "",
    vimeolink: "",
    youtubelink: ""
  });

  const cleanEditorContent = (html = "") => {
    if (!html) return "";

    let cleaned = html.trim();

    // loop because last may be [empty_line] + <p>&nbsp;</p> + [empty_line]
    let changed = true;

    while (changed) {
      changed = false;

      // remove only one trailing empty paragraph block
      const removedParagraph = cleaned.replace(
        /<p>(?:&nbsp;|\s|<br\s*\/?>)*<\/p>\s*$/i,
        ""
      );

      if (removedParagraph !== cleaned) {
        cleaned = removedParagraph.trim();
        changed = true;
      }

      // remove only one trailing [empty_line]
      const removedToken = cleaned.replace(
        /\[empty_line\]\s*$/i,
        ""
      );

      if (removedToken !== cleaned) {
        cleaned = removedToken.trim();
        changed = true;
      }
    }

    return cleaned;
  };

  const [heroSectionImage, setHeroSectionImage] = useState(null);
  const [existingHeroSectionImage, setExistingHeroSectionImage] = useState("");
  const [descSectionImage, setDescSectionImage] = useState(null);
  const [existingDescSectionImage, setExistingDescSectionImage] = useState("");
  const [howToUseSectionImage, setHowToUseSectionImage] = useState(null);
  const [existingHowToUseSectionImage, setExistingHowToUseSectionImage] =
    useState("");
  const [aboutSectionImage, setAboutSectionImage] = useState(null);
  const [existingAboutSectionImage, setExistingAboutSectionImage] =
    useState("");
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const hasFetched = useRef(false);
  const navigate = useNavigate();

  const [recordId, setRecordId] = useState(null);

  // ✅ handle text field changes
  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // ✅ handle image selection
  const handleFileChange = (e, setImage) => {
    setImage(e.target.files[0]);
  };

  // ✅ handle dynamic highlights
  const handleAddHighlight = () => {
    setFormData({
      ...formData,
      highlights: [...formData.highlights, { key: "", value: "" }],
    });
  };

  const handleRemoveHighlight = (index) => {
    const newHighlights = formData.highlights.filter((_, i) => i !== index);
    setFormData({ ...formData, highlights: newHighlights });
  };

  const handleHighlightChange = (index, field, value) => {
    const newHighlights = [...formData.highlights];
    newHighlights[index][field] = value;
    setFormData({ ...formData, highlights: newHighlights });
  };

  const handleAddAdvantage = () => {
    setFormData({
      ...formData,
      advantages: [...formData.advantages, { key: "", value: "" }],
    });
  };

  const handleRemoveAdvantage = (index) => {
    const newAdvantage = formData.advantages.filter((_, i) => i !== index);
    setFormData({ ...formData, advantages: newAdvantage });
  };

  const handleAdvantageChange = (index, field, value) => {
    const newAdvantage = [...formData.advantages];
    newAdvantage[index][field] = value;
    setFormData({ ...formData, advantages: newAdvantage });
  };

  const handleAddAboutFeatures = () => {
    setFormData({
      ...formData,
      aboutfeatures: [...formData.aboutfeatures, { key: "" }],
    });
  };

  const handleRemoveAboutFeatures = (index) => {
    const newAboutFeatures = formData.aboutfeatures.filter(
      (_, i) => i !== index
    );
    setFormData({ ...formData, aboutfeatures: newAboutFeatures });
  };

  const handleAboutFeatureChange = (index, field, value) => {
    const newAboutFeatures = [...formData.aboutfeatures];
    newAboutFeatures[index][field] = value;
    setFormData({ ...formData, aboutfeatures: newAboutFeatures });
  };

  const handleAddFeatures = () => {
    setFormData({
      ...formData,
      features: [...formData.features, { key: "", value: "", image: "" }],
    });
  };

  const handleRemoveFeatures = (index) => {
    const newFeatures = formData.features.filter((_, i) => i !== index);
    setFormData({ ...formData, features: newFeatures });
  };

  const handleFeatureChange = (index, field, value) => {
    const newFeatures = [...formData.features];
    newFeatures[index][field] = value;
    setFormData({ ...formData, features: newFeatures });
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await getHomeDetail();
      if (res.data?.success) {
        const homeDetails = res.data.data;
        setRecordId(homeDetails.id);
        setFormData({
          hero_section_title: cleanEditorContent(homeDetails.hero_section_title) || "",
          hero_section_desc: cleanEditorContent(homeDetails.hero_section_desc) || "",
          button_desc: cleanEditorContent(homeDetails.button_desc) || "",
          infotitle: cleanEditorContent(homeDetails.infotitle) || "",
          desc: cleanEditorContent(homeDetails.desc) || "",
          highlights: homeDetails.highlights || [{ key: "", value: "" }],
          highlights_enabled: homeDetails.highlights_enabled !== undefined ? homeDetails.highlights_enabled : true,
          how_use_title: cleanEditorContent(homeDetails.how_use_title) || "",
          how_use_desc: cleanEditorContent(homeDetails.how_use_desc) || "",
          about_title: cleanEditorContent(homeDetails.about_title) || "",
          about_desc: cleanEditorContent(homeDetails.about_desc) || "",
          why_choose_title: (homeDetails.why_choose_title) || "",
          why_choose_desc: homeDetails.why_choose_desc || "",
          features: homeDetails.features || [],
          aboutfeatures: homeDetails.aboutfeatures || [],
          advantages: homeDetails.advantages || [],
          tollfreenumber: homeDetails.tollfreenumber || "",
          facebooklink: homeDetails.facebooklink || "",
          twitterlink: homeDetails.twitterlink || "",
          vimeolink: homeDetails.vimeolink || "",
          youtubelink: homeDetails.youtubelink || "",
        });
        setExistingHeroSectionImage(homeDetails.hero_section_image || "");
        setExistingDescSectionImage(homeDetails.desc_section_image || "");
        setExistingHowToUseSectionImage(
          homeDetails.how_use_section_image || ""
        );
        setExistingAboutSectionImage(homeDetails.about_section_image || "");
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // ✅ fetch data for edit mode
  useEffect(() => {
    if (!hasFetched.current) {
      hasFetched.current = true;
      fetchData();
    }
  }, []);

  // ✅ validate form
  const validateForm = () => {
    const newErrors = {};
    if (!formData.hero_section_title.trim())
      newErrors.hero_section_title = "Hero Section Title is required";
    // if (!formData.hero_section_desc.trim())
    //   newErrors.hero_section_desc = "Hero Section Description is required";
    if (!formData.desc.trim())
      newErrors.desc = "What is GLAPSOL description is required";
    if (!formData.how_use_desc.trim())
      newErrors.how_use_desc = "How to use GLAPSOL description is required";
    if (!formData.about_desc.trim())
      newErrors.about_desc = "About description is required";
    if (!formData.why_choose_desc.trim())
      newErrors.why_choose_desc = "Why choose GLAPSOL description is required";
    return newErrors;
  };

  // ✅ submit handler
  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      console.log(validationErrors, "validateErrors");
      setErrors(validationErrors);
      return;
    }

    setErrors({});
    setLoading(true);

    try {
      const fd = new FormData();

      // append images
      if (heroSectionImage) fd.append("hero_section_image", heroSectionImage);
      if (descSectionImage) fd.append("desc_section_image", descSectionImage);
      if (howToUseSectionImage)
        fd.append("how_use_section_image", howToUseSectionImage);
      if (aboutSectionImage)
        fd.append("about_section_image", aboutSectionImage);

      const finalFeatures = [];

      formData.features.forEach((item, index) => {
        if (item.image instanceof File) {
          // ✅ send with index (IMPORTANT)
          fd.append(`feature_images[${index}]`, item.image);

          finalFeatures.push({
            key: item.key,
            value: item.value,
            image: null, // backend will replace
          });
        } else {
          finalFeatures.push({
            key: item.key,
            value: item.value,
            image: item.image || null,
          });
        }
      });

      // ✅ append JSON
      fd.append("features", JSON.stringify(finalFeatures));

      /* ---------------- OTHER FIELDS ---------------- */

      Object.keys(formData).forEach((key) => {
        if (["highlights", "advantages", "aboutfeatures"].includes(key)) {
          fd.append(key, JSON.stringify(formData[key]));
        } else if (key === "highlights_enabled") {
          fd.append(key, formData[key] ? "1" : "0");
        } else if (key !== "features") {
          // 👈 already handled
          fd.append(key, formData[key]);
        }
      });

      const response = recordId
        ? await updateHomeDetails(recordId, fd)
        : await createHomeDetails(fd);

      if (response.data?.success) {
        Swal.fire({
          icon: "success",
          title: "Success",
          text:
            response.data?.message ||
            `Home Page ${recordId ? "updated" : "added"} successfully!`,
        });
        fetchData();
      } else {
        throw new Error(response.data?.message || "Unknown error");
      }
    } catch (err) {
      console.error("Error while saving:", err);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: err.message || "Something went wrong while saving",
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div id="preloader">
        <div className="sk-three-bounce">
          <div className="sk-child sk-bounce1"></div>
          <div className="sk-child sk-bounce2"></div>
          <div className="sk-child sk-bounce3"></div>
        </div>
      </div>
    );
  }

  const handleFeatureImageChange = (index, file) => {
    const copy = [...formData.features];

    copy[index].image = file; // Store File object

    setFormData({
      ...formData,
      features: copy,
    });
  };

  return (
    <>
      <PageTitle
        activeMenu="Home Page Details"
        motherMenu="Home Page Details"
        activeMenuLink="home-page"
        motherMenuLink="home-page"
      />
      <div className="row">
        <div className="col-xl-12">
          <div className="card">
            <div className="card-body">
              <form onSubmit={handleSubmit} encType="multipart/form-data">
                <div
                  onClick={() => navigate("/dashboard")}
                  className="mb-4 d-flex align-items-center flex-wrap gap-2 cursor-pointer"
                >
                  <i className="fas fa-chevron-left"></i>
                  <div>Dashboard</div>
                </div>

                {/* Hero Section */}
                <div className="d-flex flex-column mb-3">
                  <label className="form-label fw-semibold">
                    Hero Section Image / Video
                  </label>

                  <HeroMediaPreview
                    file={heroSectionImage}
                    mediaUrl={existingHeroSectionImage}
                    noImage={NoImage}
                    height="200px"
                    width="200px"
                  />

                  <input
                    type="file"
                    accept="image/*,video/*"
                    onChange={(e) => handleFileChange(e, setHeroSectionImage)}
                    id="heroImage"
                    className="d-none"
                  />

                  <label htmlFor="heroImage" className="btn btn-primary mt-3 w-max">
                    Select Image / Video
                  </label>
                </div>

                <div className="mb-3">
                  <label className="form-label fw-semibold">
                    Hero Section Title
                  </label>
                  <div className="custom-ekeditor cms-radius add-content-ckeditor mb-3">
                    <CKEditor
                      editor={CustomClassicEditor}
                      data={(formData.hero_section_title).replace(/\[empty_line\]/g, '<div data-empty-line="true"></div>') || ""}
                      onChange={(event, editor) => {
                        setFormData({
                          ...formData,
                          hero_section_title: editor.getData(),
                        });
                      }}
                    />
                    {/* <CkEditorBlog
                      value={formData.hero_section_title}
                      onChange={(val) =>
                        setFormData((prev) => ({
                          ...prev,
                          hero_section_title: val,
                        }))
                      }
                      error={errors.hero_section_title}
                    /> */}
                  </div>
                  {errors.hero_section_title && (
                    <div className="invalid-feedback">
                      {errors.hero_section_title}
                    </div>
                  )}
                </div>

                <div className="mb-3">
                  <label className="form-label fw-semibold">
                    Hero Section Description
                  </label>
                  <div className="custom-ekeditor cms-radius add-content-ckeditor mb-3">
                    <CKEditor
                      editor={CustomClassicEditor}
                      data={(formData.hero_section_desc).replace(/\[empty_line\]/g, '<div data-empty-line="true"></div>') || ""}
                      onChange={(event, editor) => {
                        setFormData({
                          ...formData,
                          hero_section_desc: editor.getData(),
                        });
                      }}
                    />
                    {/* <CkEditorBlog
                      value={formData.hero_section_desc}
                      onChange={(val) =>
                        setFormData((prev) => ({
                          ...prev,
                          hero_section_desc: val,
                        }))
                      }
                      error={errors.hero_section_desc}
                    /> */}
                  </div>
                  {errors.hero_section_desc && (
                    <div className="invalid-feedback">
                      {errors.hero_section_desc}
                    </div>
                  )}
                </div>

                <div className="mb-3">
                  <label className="form-label fw-semibold">
                    Hero Section Button Description
                  </label>
                  <div className="custom-ekeditor cms-radius add-content-ckeditor mb-3">
                    <CKEditor
                      editor={CustomClassicEditor}
                      data={(formData.button_desc).replace(/\[empty_line\]/g, '<div data-empty-line="true"></div>') || ""}
                      onChange={(event, editor) => {
                        setFormData({
                          ...formData,
                          button_desc: editor.getData(),
                        });
                      }}
                    />
                    {/* <CkEditorBlog
                      value={formData.button_desc}
                      onChange={(val) =>
                        setFormData((prev) => ({
                          ...prev,
                          button_desc: val,
                        }))
                      }
                      error={errors.button_desc}
                    /> */}
                  </div>
                  {errors.button_desc && (
                    <div className="invalid-feedback">
                      {errors.button_desc}
                    </div>
                  )}
                </div>

                {/* Highlights Section */}
                <div className="mb-4">
                  <div className="d-flex align-items-center justify-content-between mb-3">
                    <label className="form-label fw-semibold mb-0">Highlights</label>
                    <div className="d-flex align-items-center">
                      <span className="me-2" style={{ fontSize: "14px" }}>
                        {formData.highlights_enabled ? "Enabled" : "Disabled"}
                      </span>
                      <div className="form-check form-switch">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          role="switch"
                          id="highlightsToggle"
                          checked={formData.highlights_enabled}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              highlights_enabled: e.target.checked,
                            })
                          }
                          style={{ width: "3rem", height: "1.5rem", cursor: "pointer" }}
                        />
                      </div>
                    </div>
                  </div>
                  {formData.highlights_enabled && formData.highlights.map((item, index) => (
                    <div
                      key={index}
                      className="position-relative border rounded p-3 mb-3"
                    >
                      <button
                        type="button"
                        className="btn btn-sm btn-danger position-absolute top-0 end-0 m-2"
                        onClick={() => handleRemoveHighlight(index)}
                      >
                        X
                      </button>
                      <div className="mb-3 mt-1">
                        <label className="form-label fw-semibold">Title</label>
                        <textarea
                          rows={2}
                          placeholder="Key"
                          className="form-control"
                          value={item.key}
                          onChange={(e) =>
                            handleHighlightChange(index, "key", e.target.value)
                          }
                        ></textarea>
                      </div>
                      <div className="mb-3">
                        <label className="form-label fw-semibold">
                          Description
                        </label>
                        <textarea
                          rows={2}
                          placeholder="Value"
                          className="form-control"
                          value={item.value}
                          onChange={(e) =>
                            handleHighlightChange(
                              index,
                              "value",
                              e.target.value
                            )
                          }
                        ></textarea>
                      </div>
                    </div>
                  ))}
                  {formData.highlights_enabled && (
                    <div>
                      <button
                        type="button"
                        className="btn btn-primary mt-2 block"
                        onClick={handleAddHighlight}
                      >
                        + Add Highlight
                      </button>
                    </div>
                  )}
                </div>

                <div className="mb-3">
                  <label>What is GLAPSOL Section Title</label>
                  <CKEditor
                    editor={CustomClassicEditor}
                    data={(formData.infotitle).replace(/\[empty_line\]/g, '<div data-empty-line="true"></div>') || ""}
                    onChange={(event, editor) => {
                      setFormData({
                        ...formData,
                        infotitle: editor.getData(),
                      });
                    }}
                  />
                  {/* <CkEditorBlog
                    value={formData.infotitle}
                    onChange={(val) =>
                      setFormData((prev) => ({
                        ...prev,
                        infotitle: val,
                      }))
                    }
                    error={errors.infotitle}
                  /> */}
                </div>

                <div className="mb-3">
                  <label className="form-label fw-semibold">
                    What is GLAPSOL Section Image
                  </label>
                  <div>
                    <img
                      src={
                        descSectionImage
                          ? URL.createObjectURL(descSectionImage)
                          : existingDescSectionImage || NoImage
                      }
                      alt="Hero"
                      width={100}
                      height={100}
                      className="rounded border mb-2"
                    />
                  </div>
                  <input
                    type="file"
                    onChange={(e) => handleFileChange(e, setDescSectionImage)}
                    id="descSectionImage"
                    className="d-none"
                  />
                  <label htmlFor="descSectionImage" className="btn btn-primary">
                    Select Image
                  </label>
                </div>

                <div className="mb-3">
                  <label>What is GLAPSOL Section Description</label>
                  <div className="custom-ekeditor cms-radius add-content-ckeditor mb-3">
                    <CKEditor
                      editor={CustomClassicEditor}
                      data={(formData.desc).replace(/\[empty_line\]/g, '<div data-empty-line="true"></div>') || ""}
                      onChange={(event, editor) => {
                        setFormData({
                          ...formData,
                          desc: editor.getData(),
                        });
                      }}
                    />
                    {/* <CkEditorBlog
                      value={formData.desc}
                      onChange={(val) =>
                        setFormData((prev) => ({
                          ...prev,
                          desc: val,
                        }))
                      }
                      error={errors.desc}
                    /> */}
                  </div>
                </div>

                <div className="mb-3">
                  <div className="mb-3">
                    <label>How to Use GLAPSOL Section Title</label>
                    <CKEditor
                      editor={CustomClassicEditor}
                      data={(formData.how_use_title).replace(/\[empty_line\]/g, '<div data-empty-line="true"></div>') || ""}
                      onChange={(event, editor) => {
                        setFormData({
                          ...formData,
                          how_use_title: editor.getData(),
                        });
                      }}
                    />
                    {/* <CkEditorBlog
                      value={formData.how_use_title}
                      onChange={(val) =>
                        setFormData((prev) => ({
                          ...prev,
                          how_use_title: val,
                        }))
                      }
                      error={errors.how_use_title}
                    /> */}
                  </div>
                  <label className="form-label fw-semibold">
                    How to Use GLAPSOL Section Image
                  </label>
                  <div>
                    <img
                      src={
                        howToUseSectionImage
                          ? URL.createObjectURL(howToUseSectionImage)
                          : existingHowToUseSectionImage || NoImage
                      }
                      alt="Hero"
                      width={100}
                      height={100}
                      className="rounded border mb-2"
                    />
                  </div>
                  <input
                    type="file"
                    onChange={(e) =>
                      handleFileChange(e, setHowToUseSectionImage)
                    }
                    id="howToUseSectionImage"
                    className="d-none"
                  />
                  <label
                    htmlFor="howToUseSectionImage"
                    className="btn btn-primary"
                  >
                    Select Image
                  </label>
                </div>

                <div className="mb-3">
                  <label>How to Use GLAPSOL Section Description</label>
                  <div className="custom-ekeditor cms-radius add-content-ckeditor mb-3">
                    <CKEditor
                      editor={CustomClassicEditor}
                      data={(formData.how_use_desc).replace(/\[empty_line\]/g, '<div data-empty-line="true"></div>') || ""}
                      onChange={(event, editor) => {
                        setFormData({
                          ...formData,
                          how_use_desc: editor.getData(),
                        });
                      }}
                    />
                    {/* <CkEditorBlog
                      value={formData.how_use_desc}
                      onChange={(val) =>
                        setFormData((prev) => ({
                          ...prev,
                          how_use_desc: val,
                        }))
                      }
                      error={errors.how_use_desc}
                    /> */}
                  </div>
                </div>

                <div className="mb-3">
                  <div className="mb-3">
                    <label>About GLAPSOL Section Title</label>
                    <CKEditor
                      editor={CustomClassicEditor}
                      data={(formData.about_title).replace(/\[empty_line\]/g, '<div data-empty-line="true"></div>') || ""}
                      onChange={(event, editor) => {
                        setFormData({
                          ...formData,
                          about_title: editor.getData(),
                        });
                      }}
                    />
                    {/* <CkEditorBlog
                      value={formData.about_title}
                      onChange={(val) =>
                        setFormData((prev) => ({
                          ...prev,
                          about_title: val,
                        }))
                      }
                      error={errors.about_title}
                    /> */}
                  </div>
                  <label className="form-label fw-semibold">
                    About GLAPSOL Section Image
                  </label>
                  <div>
                    <img
                      src={
                        aboutSectionImage
                          ? URL.createObjectURL(aboutSectionImage)
                          : existingAboutSectionImage || NoImage
                      }
                      alt="Hero"
                      width={100}
                      height={100}
                      className="rounded border mb-2"
                    />
                  </div>
                  <input
                    type="file"
                    onChange={(e) => handleFileChange(e, setAboutSectionImage)}
                    id="aboutSectionImage"
                    className="d-none"
                  />
                  <label
                    htmlFor="aboutSectionImage"
                    className="btn btn-primary"
                  >
                    Select Image
                  </label>
                </div>

                <div className="mb-3">
                  <label>About GLAPSOL Section Description</label>
                  <CKEditor
                    editor={CustomClassicEditor}
                    data={(formData.about_desc).replace(/\[empty_line\]/g, '<div data-empty-line="true"></div>') || ""}
                    onChange={(event, editor) => {
                      setFormData({
                        ...formData,
                        about_desc: editor.getData(),
                      });
                    }}
                  />
                  {/* <CkEditorBlog
                    value={formData.about_desc}
                    onChange={(val) =>
                      setFormData((prev) => ({
                        ...prev,
                        about_desc: val,
                      }))
                    }
                    error={errors.about_desc}
                  /> */}
                  {/* <textarea
                    name="about_desc"
                    rows={2}
                    placeholder="Key"
                    className="form-control"
                    value={formData.about_desc}
                    onChange={handleChange}
                  ></textarea> */}
                </div>

                <div className="mb-4">
                  <label className="form-label">About Features</label>
                  {formData.aboutfeatures.map((item, index) => (
                    <div
                      key={index}
                      className="position-relative border rounded p-3 mb-3"
                    >
                      <button
                        type="button"
                        className="btn btn-sm btn-danger position-absolute top-0 end-0 m-2"
                        onClick={() => handleRemoveAboutFeatures(index)}
                      >
                        X
                      </button>
                      <div className="mb-3 mt-1">
                        <label className="form-label fw-semibold">Title</label>
                        <textarea
                          rows={2}
                          placeholder="Key"
                          className="form-control"
                          value={item.key}
                          onChange={(e) =>
                            handleAboutFeatureChange(
                              index,
                              "key",
                              e.target.value
                            )
                          }
                        ></textarea>
                      </div>
                    </div>
                  ))}
                  <div>
                    <button
                      type="button"
                      className="btn btn-primary mt-2 block"
                      onClick={handleAddAboutFeatures}
                    >
                      + Add About Features
                    </button>
                  </div>
                </div>

                <div className="mb-3">
                  <label>Why choose GLAPSOL Title</label>
                  <CKEditor
                    editor={CustomClassicEditor}
                    data={(formData.why_choose_title).replace(/\[empty_line\]/g, '<div data-empty-line="true"></div>') || ""}
                    onChange={(event, editor) => {
                      setFormData({
                        ...formData,
                        why_choose_title: editor.getData(),
                      });
                    }}
                  />
                  {/* <CkEditorBlog
                    value={formData.why_choose_title}
                    onChange={(val) =>
                      setFormData((prev) => ({
                        ...prev,
                        why_choose_title: val,
                      }))
                    }
                    error={errors.why_choose_title}
                  /> */}
                </div>

                <div className="mb-3">
                  <label>Why choose GLAPSOL Section Description</label>
                  <textarea
                    name="why_choose_desc"
                    rows={2}
                    placeholder="Key"
                    className="form-control"
                    value={formData.why_choose_desc}
                    onChange={handleChange}
                  ></textarea>
                </div>

                <div className="mb-4">
                  <label className="form-label">Why choose GLAPSOL Features</label>
                  {formData.features.map((item, index) => (
                    <div
                      key={index}
                      className="position-relative border rounded p-3 mb-3"
                    >
                      <button
                        type="button"
                        className="btn btn-sm btn-danger position-absolute top-0 end-0 m-2"
                        onClick={() => handleRemoveFeatures(index)}
                      >
                        X
                      </button>
                      <div className="mb-3 mt-1">
                        <label className="form-label fw-semibold">Title</label>
                        <textarea
                          rows={2}
                          placeholder="Key"
                          className="form-control"
                          value={item.key}
                          onChange={(e) =>
                            handleFeatureChange(index, "key", e.target.value)
                          }
                        ></textarea>
                      </div>
                      <div className="mb-3">
                        <label className="form-label fw-semibold">
                          Description
                        </label>
                        <textarea
                          rows={2}
                          placeholder="Value"
                          className="form-control"
                          value={item.value}
                          onChange={(e) =>
                            handleFeatureChange(index, "value", e.target.value)
                          }
                        ></textarea>
                      </div>
                      <div className="mb-3">
                        <label className="form-label fw-semibold">
                          Feature Image
                        </label>

                        <input
                          type="file"
                          accept="image/*"
                          className="form-control"
                          onChange={(e) =>
                            handleFeatureImageChange(
                              index,
                              e.target.files[0]
                            )
                          }
                        />
                      </div>

                      {/* Preview */}
                      {item.image && (
                        <div className="mb-2">
                          <small className="text-muted">
                            Preview:
                          </small>

                          <div className="mt-1">
                            <img
                              src={
                                item.image instanceof File
                                  ? URL.createObjectURL(item.image)
                                  : item.image // URL from API
                              }
                              alt="Feature"
                              style={{
                                width: "120px",
                                height: "80px",
                                objectFit: "cover",
                                borderRadius: "6px",
                                border: "1px solid #ddd",
                              }}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                  <div>
                    <button
                      type="button"
                      className="btn btn-primary mt-2 block"
                      onClick={handleAddFeatures}
                    >
                      + Add Features
                    </button>
                  </div>
                </div>

                <div className="mb-4">
                  <label className="form-label">Advantages</label>
                  {formData.advantages.map((item, index) => (
                    <div
                      key={index}
                      className="position-relative border rounded p-3 mb-3"
                    >
                      <button
                        type="button"
                        className="btn btn-sm btn-danger position-absolute top-0 end-0 m-2"
                        onClick={() => handleRemoveAdvantage(index)}
                      >
                        X
                      </button>
                      <div className="mb-3 mt-1">
                        <label className="form-label fw-semibold">Title</label>
                        <textarea
                          rows={2}
                          placeholder="Key"
                          className="form-control"
                          value={item.key}
                          onChange={(e) =>
                            handleAdvantageChange(index, "key", e.target.value)
                          }
                        ></textarea>
                      </div>
                      <div className="mb-3">
                        <label className="form-label fw-semibold">
                          Description
                        </label>
                        <textarea
                          rows={2}
                          placeholder="Value"
                          className="form-control"
                          value={item.value}
                          onChange={(e) =>
                            handleAdvantageChange(
                              index,
                              "value",
                              e.target.value
                            )
                          }
                        ></textarea>
                      </div>
                    </div>
                  ))}
                  <div>
                    <button
                      type="button"
                      className="btn btn-primary mt-2 block"
                      onClick={handleAddAdvantage}
                    >
                      + Add Advantage
                    </button>
                  </div>
                </div>

                <div className="mb-3">
                  <label className="form-label fw-semibold">Toll Free Number</label>
                  <input
                    name="tollfreenumber"
                    type="text"
                    className={`form-control w-full ${errors.tollfreenumber ? "is-invalid" : ""
                      }`}
                    placeholder="Name"
                    value={formData.tollfreenumber} // bind value
                    onChange={handleChange} // update state
                  />
                  {errors.tollfreenumber && (
                    <div className="invalid-feedback mt-1">
                      {errors.tollfreenumber}
                    </div>
                  )}
                </div>

                <div className="mb-3">
                  <label className="form-label fw-semibold">Facebook Link</label>
                  <input
                    name="facebooklink"
                    type="text"
                    className={`form-control w-full ${errors.facebooklink ? "is-invalid" : ""
                      }`}
                    placeholder="Name"
                    value={formData.facebooklink} // bind value
                    onChange={handleChange} // update state
                  />
                  {errors.facebooklink && (
                    <div className="invalid-feedback mt-1">
                      {errors.facebooklink}
                    </div>
                  )}
                </div>

                <div className="mb-3">
                  <label className="form-label fw-semibold">Twitter Link</label>
                  <input
                    name="twitterlink"
                    type="text"
                    className={`form-control w-full ${errors.twitterlink ? "is-invalid" : ""
                      }`}
                    placeholder="Name"
                    value={formData.twitterlink} // bind value
                    onChange={handleChange} // update state
                  />
                  {errors.twitterlink && (
                    <div className="invalid-feedback mt-1">
                      {errors.twitterlink}
                    </div>
                  )}
                </div>

                <div className="mb-3">
                  <label className="form-label fw-semibold">Vimeo Link</label>
                  <input
                    name="vimeolink"
                    type="text"
                    className={`form-control w-full ${errors.vimeolink ? "is-invalid" : ""
                      }`}
                    placeholder="Name"
                    value={formData.vimeolink} // bind value
                    onChange={handleChange} // update state
                  />
                  {errors.vimeolink && (
                    <div className="invalid-feedback mt-1">
                      {errors.vimeolink}
                    </div>
                  )}
                </div>

                <div className="mb-3">
                  <label className="form-label fw-semibold">Youtube Link</label>
                  <input
                    name="youtubelink"
                    type="text"
                    className={`form-control w-full ${errors.youtubelink ? "is-invalid" : ""
                      }`}
                    placeholder="Name"
                    value={formData.youtubelink} // bind value
                    onChange={handleChange} // update state
                  />
                  {errors.youtubelink && (
                    <div className="invalid-feedback mt-1">
                      {errors.youtubelink}
                    </div>
                  )}
                </div>

                {/* Footer Buttons */}
                <hr />
                <div className="text-end">
                  {/* <Button
                    type="button"
                    variant="secondary"
                    className="me-2"
                    onClick={() => navigate("/bank")}
                  >
                    Back
                  </Button> */}
                  <Button type="submit" variant="primary">
                    {recordId ? "Update" : "Save"}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default HomePage;
